/**
 * FAB clearance (#3633, D-203 option a) — pure helpers behind
 * components/fabMenu/useFabPracticeClearance.js. Zero React/DOM imports so the
 * geometry stays unit-testable in a plain node vitest run.
 *
 * DECISION (D-203, 2026-09-29): on phones the round FabMenu trigger is
 * `position: fixed` bottom-left, so Practice buttons scroll under it. The
 * decided fix is to HIDE the trigger while a Practice button is on screen in
 * the trigger's zone. "In the trigger's zone" is the deliberately narrow
 * reading: the trigger's own footprint plus FAB_CLEARANCE_PAD_PX of finger
 * margin, NOT "a Practice button is anywhere on screen" (which on a phone is
 * most scroll positions and would leave help/bug-report unreachable).
 *
 * WHAT COUNTS AS A BUTTON TO CLEAR (a Practice button, and since D-208 / #3676 every other
 * main call-to-action button on the dashboard cards): any element that opts in with the
 * `data-fab-avoid` attribute. Declarative on purpose: the elements are mounted
 * late and unmounted freely (async data, modals, route changes), so the hook
 * discovers them with a MutationObserver instead of a one-time query and a
 * component only has to say what it is.
 */

// The opt-in marker. `data-fab-avoid="cta"` on a main call-to-action button the phone FabMenu
// disc must not cover. The value was "practice" until #3676 (D-208 widened the rule from
// "buttons that start a practice run" to every main CTA button on the dashboard cards); the
// hook matches on the attribute's presence, never its value, so the value is documentation
// and the per-element pin test (lib/__tests__/fabAvoidMarkers3633.test.js) asserts it.
export const FAB_AVOID_ATTRIBUTE = 'data-fab-avoid';
// The value every marked element carries today.
export const FAB_AVOID_VALUE = 'cta';
export const FAB_AVOID_SELECTOR = `[${FAB_AVOID_ATTRIBUTE}]`;

// Finger margin around the trigger's box. A button whose edge is closer than
// this to the disc is still hard to tap without hitting the disc.
export const FAB_CLEARANCE_PAD_PX = 8;

/**
 * The IntersectionObserver `rootMargin` that shrinks the viewport root down to
 * the FAB's zone. Negative margins inset each root edge; the result is a root
 * rect equal to `zone`, so `isIntersecting` means "overlaps the FAB's zone",
 * and it keeps updating on ANY geometry change (page scroll, a carousel
 * scrolled sideways, layout shift) with no scroll listener.
 *
 * @param {{left:number,top:number,right:number,bottom:number}} rect  the trigger's box, viewport coords
 * @param {{width:number,height:number}} viewport  the viewport the fixed trigger is placed in
 * @param {number} [pad]
 * @returns {string|null} a rootMargin string, or null when the geometry is unusable
 *   (zero-size trigger, non-finite numbers): callers must treat null as "cannot observe", never as "clear".
 */
export function zoneRootMargin(rect, viewport, pad = FAB_CLEARANCE_PAD_PX) {
  if (!rect || !viewport) return null;
  const nums = [rect.left, rect.top, rect.right, rect.bottom, viewport.width, viewport.height];
  if (!nums.every(Number.isFinite)) return null;
  if (rect.right - rect.left <= 0 || rect.bottom - rect.top <= 0) return null;
  const left = Math.max(0, rect.left - pad);
  const top = Math.max(0, rect.top - pad);
  const right = Math.min(viewport.width, rect.right + pad);
  const bottom = Math.min(viewport.height, rect.bottom + pad);
  // rootMargin order: top right bottom left. Each value is how far that root edge is pulled in from the
  // viewport edge. Left/top round down and right/bottom round up so the zone is never smaller than the disc.
  const inset = (n) => (n > 0 ? `-${n}px` : '0px');
  return [
    inset(Math.floor(top)),
    inset(viewport.width - Math.ceil(right)),
    inset(viewport.height - Math.ceil(bottom)),
    inset(Math.floor(left)),
  ].join(' ');
}

/**
 * The same zone as a plain rect, for the rendered-position specs and for
 * tests that must state the zone independently of the observer.
 */
export function zoneRect(rect, viewport, pad = FAB_CLEARANCE_PAD_PX) {
  return {
    left: Math.max(0, rect.left - pad),
    top: Math.max(0, rect.top - pad),
    right: Math.min(viewport.width, rect.right + pad),
    bottom: Math.min(viewport.height, rect.bottom + pad),
  };
}
